import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  Button,
  Link,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBack from "@mui/icons-material/ArrowBack";

function getUsernameOtpButtonLabel(usernameOtpSent) {
  if (usernameOtpSent) return "Verify";
  return "Send OTP";
}

function getPasswordResetButtonLabel(otpSent) {
  if (otpSent) return "Reset password";
  return "Send OTP";
}

const fieldSx = (primaryColor) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "0px",
    backgroundColor: "rgba(249, 249, 249, 0.8)",
    "& fieldset": { borderColor: "#E0E0E0", borderWidth: "2px" },
    "&:hover fieldset": { borderColor: primaryColor },
    "&.Mui-focused fieldset": { borderColor: primaryColor },
  },
});

const shouldCheckLoginAccounts = (login) => !login.forgotUsername && !login.forgotPassword && !login.needs2FA;

const handleUsernameBlur = (login) => (e) => {
  const v = e.target.value;
  login.setEmail(v);
  if (v.trim()) {
    login.setEmailError("");
  } else {
    login.setEmailError("Username is required");
  }
  if (shouldCheckLoginAccounts(login)) {
    login.checkLoginAccountsByEmail(v);
  }
};

const TwoFactorStep = ({ login, tfSx }) => {
  const { otp2FA, setOtp2FA, otp2FAError, loading, primaryColor, secondaryColor, handle2FAVerification, handleKeyDown } = login;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} onKeyDown={handleKeyDown}>
      <TextField
        fullWidth
        label="Verification code"
        value={otp2FA}
        onChange={(e) => setOtp2FA(e.target.value)}
        error={!!otp2FAError}
        helperText={otp2FAError}
        sx={tfSx}
      />
      <Button
        variant="contained"
        fullWidth
        disabled={loading}
        onClick={handle2FAVerification}
        sx={{ bgcolor: primaryColor, "&:hover": { bgcolor: secondaryColor }, borderRadius: 0, py: 1.2, textTransform: "none", fontWeight: 600 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Verify & sign in"}
      </Button>
    </Box>
  );
};

const RecoveredUsernameView = ({ login }) => {
  const { retrievedUsername, primaryColor } = login;
  return (
    <>
      <Typography variant="body1">
        Your username: <strong>{retrievedUsername}</strong>
      </Typography>
      <Button variant="contained" fullWidth onClick={login.handleBackToLogin} sx={{ bgcolor: primaryColor, borderRadius: 0, textTransform: "none" }}>
        Back to login
      </Button>
    </>
  );
};

const UsernameRecoveryForm = ({ login, tfSx }) => {
  const {
    email, setEmail, emailError, usernameOtpSent, otp, setOtp, otpError,
    loading, primaryColor, secondaryColor, handleVerifyUsernameOTP, handleSendUsernameOTP,
  } = login;
  return (
    <>
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!emailError}
        helperText={emailError}
        sx={tfSx}
      />
      {usernameOtpSent && (
        <TextField
          fullWidth
          label="OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          error={!!otpError}
          helperText={otpError}
          sx={tfSx}
        />
      )}
      <Button
        variant="contained"
        fullWidth
        disabled={loading}
        onClick={usernameOtpSent ? handleVerifyUsernameOTP : handleSendUsernameOTP}
        sx={{ bgcolor: primaryColor, "&:hover": { bgcolor: secondaryColor }, borderRadius: 0, py: 1.2, textTransform: "none", fontWeight: 600 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : getUsernameOtpButtonLabel(usernameOtpSent)}
      </Button>
      <IconButton onClick={login.handleBackToLogin} sx={{ alignSelf: "flex-start" }}>
        <ArrowBack data-testid="ArrowBackIcon" fontSize="small" />
        <Typography variant="body2" sx={{ color: primaryColor, ml: 0.5 }}>Back to login</Typography>
      </IconButton>
    </>
  );
};

const ForgotUsernameStep = ({ login, tfSx }) => {
  const { handleKeyDown, retrievedUsername } = login;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} onKeyDown={handleKeyDown}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Recover your username
      </Typography>
      {retrievedUsername ? (
        <RecoveredUsernameView login={login} />
      ) : (
        <UsernameRecoveryForm login={login} tfSx={tfSx} />
      )}
    </Box>
  );
};

const PasswordResetOtpFields = ({ login, tfSx }) => {
  const {
    otp, setOtp, otpError, newPassword, setNewPassword, newPasswordError,
    confirmPassword, setConfirmPassword, confirmPasswordError,
  } = login;
  return (
    <>
      <TextField
        fullWidth
        label="OTP"
        value={otp}
        onChange={(e) => setOtp(e.target.value)}
        error={!!otpError}
        helperText={otpError}
        sx={tfSx}
      />
      <TextField
        fullWidth
        label="New password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        error={!!newPasswordError}
        helperText={newPasswordError}
        sx={tfSx}
      />
      <TextField
        fullWidth
        label="Confirm password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={!!confirmPasswordError}
        helperText={confirmPasswordError}
        sx={tfSx}
      />
    </>
  );
};

const ForgotPasswordStep = ({ login, tfSx }) => {
  const {
    handleKeyDown, email, setEmail, emailError, otpSent, loading, primaryColor, secondaryColor,
    handleResetPassword, handleOnclickSendOTP,
  } = login;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} onKeyDown={handleKeyDown}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Reset your password
      </Typography>
      <TextField
        fullWidth
        label="Username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!emailError}
        helperText={emailError}
        sx={tfSx}
      />
      {otpSent && <PasswordResetOtpFields login={login} tfSx={tfSx} />}
      <Button
        variant="contained"
        fullWidth
        disabled={loading}
        onClick={otpSent ? handleResetPassword : handleOnclickSendOTP}
        sx={{ bgcolor: primaryColor, "&:hover": { bgcolor: secondaryColor }, borderRadius: 0, py: 1.2, textTransform: "none", fontWeight: 600 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : getPasswordResetButtonLabel(otpSent)}
      </Button>
      <IconButton onClick={login.handleBackToLogin} sx={{ alignSelf: "flex-start" }}>
        <ArrowBack data-testid="ArrowBackIcon" fontSize="small" />
        <Typography variant="body2" sx={{ color: primaryColor, ml: 0.5 }}>Back to login</Typography>
      </IconButton>
    </Box>
  );
};

const AccountListItem = ({ account, login }) => (
  <ListItem disablePadding sx={{ mb: 0.5 }}>
    <ListItemButton
      onClick={() => login.pickAccountRecoverUsername(account)}
      sx={{ borderRadius: 1, py: 0.5 }}
    >
      <ListItemAvatar sx={{ minWidth: 36 }}>
        <Avatar sx={{ width: 28, height: 28, bgcolor: login.primaryColor, fontSize: 14 }}>
          {(account.username || "?").charAt(0).toUpperCase()}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={account.username}
        secondary={
          [account.role, account.lawfirm_or_pg_label].filter(Boolean).join(" · ")
          || "Vermost user"
        }
      />
    </ListItemButton>
  </ListItem>
);

const LoginAccountsHint = ({ login }) => (
  <Box
    sx={{
      mt: 0,
      mb: 0,
      p: 1.5,
      borderRadius: 1,
      backgroundColor: "rgba(255, 167, 38, 0.08)",
      border: "1px solid rgba(255, 167, 38, 0.3)",
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
      We found {login.loginAccountsList.length} accounts on this email
    </Typography>
    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
      Enter your username above, or pick one of these to recover its username via email:
    </Typography>
    <List dense sx={{ width: "100%", py: 0 }}>
      {login.loginAccountsList.map((account) => (
        <AccountListItem key={account.user_id} account={account} login={login} />
      ))}
    </List>
  </Box>
);

const DefaultLoginStep = ({ login, tfSx }) => {
  const {
    email, setEmail, emailError, password, setPassword, passwordError, showPassword, setShowPassword,
    rememberMe, setRememberMe, loading, primaryColor, secondaryColor, handleLogin, handleKeyDown,
    loginAccountsList, forgotPassword, forgotUsername, needs2FA, handleForgotPassword,
    handleForgotUsernameLink, navigate,
  } = login;

  const showAccountsHint = !forgotPassword && !forgotUsername && !needs2FA && loginAccountsList.length > 1;

  return (
    <Box
      component="form"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      onSubmit={(e) => {
        e.preventDefault();
        handleLogin();
      }}
      onKeyDown={handleKeyDown}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Sign in to your account
      </Typography>
      <TextField
        fullWidth
        label="Username"
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={handleUsernameBlur(login)}
        error={!!emailError}
        helperText={emailError}
        sx={tfSx}
      />
      {showAccountsHint && <LoginAccountsHint login={login} />}
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!passwordError}
        helperText={passwordError}
        sx={tfSx}
        slotProps={{ input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}}
      />
      <FormControlLabel
        control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} size="small" />}
        label="Remember me"
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{
          bgcolor: primaryColor,
          "&:hover": { bgcolor: secondaryColor },
          borderRadius: 0,
          py: 1.2,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Sign in"}
      </Button>
      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Link component="button" type="button" onClick={handleForgotPassword} sx={{ color: primaryColor, fontWeight: 600, textDecoration: "none", fontSize: "0.9rem" }}>
          Forgot password?
        </Link>
        <Link component="button" type="button" onClick={handleForgotUsernameLink} sx={{ color: primaryColor, fontWeight: 600, textDecoration: "none", fontSize: "0.9rem" }}>
          Forgot username?
        </Link>
      </Box>
      <Button
        variant="outlined"
        fullWidth
        onClick={login.handleCreateAccount}
        sx={{
          borderColor: primaryColor,
          color: primaryColor,
          "&:hover": { borderColor: secondaryColor, color: secondaryColor },
          borderRadius: 0,
          py: 1,
          textTransform: "none",
          fontWeight: 600,
          mt: 1,
        }}
      >
        Create Account
      </Button>
      <Typography variant="body2" sx={{ textAlign: "center", mt: 1, color: "text.secondary", lineHeight: 1.5 }}>
        Need a bond without signing in?{" "}
        <Link
          component="button"
          type="button"
          onClick={() => navigate("/guest-request")}
          sx={{
            color: primaryColor,
            fontWeight: 600,
            textDecoration: "none",
            fontSize: "inherit",
            verticalAlign: "baseline",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Request a bond as a guest
        </Link>
      </Typography>
    </Box>
  );
};

const LoginFormFields = ({ login }) => {
  const { primaryColor, needs2FA, forgotUsername, forgotPassword } = login;
  const tfSx = fieldSx(primaryColor);

  if (needs2FA) {
    return <TwoFactorStep login={login} tfSx={tfSx} />;
  }

  if (forgotUsername) {
    return <ForgotUsernameStep login={login} tfSx={tfSx} />;
  }

  if (forgotPassword) {
    return <ForgotPasswordStep login={login} tfSx={tfSx} />;
  }

  return <DefaultLoginStep login={login} tfSx={tfSx} />;
};

LoginFormFields.propTypes = {
  login: PropTypes.shape({
    email: PropTypes.string,
    setEmail: PropTypes.func,
    password: PropTypes.string,
    setPassword: PropTypes.func,
    showPassword: PropTypes.bool,
    setShowPassword: PropTypes.func,
    loading: PropTypes.bool,
    forgotPassword: PropTypes.bool,
    forgotUsername: PropTypes.bool,
    needs2FA: PropTypes.bool,
    rememberMe: PropTypes.bool,
    setRememberMe: PropTypes.func,
    primaryColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    emailError: PropTypes.string,
    setEmailError: PropTypes.func,
    passwordError: PropTypes.string,
    newPassword: PropTypes.string,
    setNewPassword: PropTypes.func,
    confirmPassword: PropTypes.string,
    setConfirmPassword: PropTypes.func,
    newPasswordError: PropTypes.string,
    confirmPasswordError: PropTypes.string,
    otp: PropTypes.string,
    setOtp: PropTypes.func,
    otpError: PropTypes.string,
    otp2FA: PropTypes.string,
    setOtp2FA: PropTypes.func,
    otp2FAError: PropTypes.string,
    usernameOtpSent: PropTypes.bool,
    retrievedUsername: PropTypes.string,
    otpSent: PropTypes.bool,
    handleLogin: PropTypes.func,
    handle2FAVerification: PropTypes.func,
    handleForgotPassword: PropTypes.func,
    handleForgotUsernameLink: PropTypes.func,
    handleKeyDown: PropTypes.func,
    handleOnclickSendOTP: PropTypes.func,
    handleSendUsernameOTP: PropTypes.func,
    handleResetPassword: PropTypes.func,
    handleBackToLogin: PropTypes.func,
    handleBackToLoginFromForgotUsername: PropTypes.func,
    handleCreateAccount: PropTypes.func,
    loginAccountsList: PropTypes.array,
    checkLoginAccountsByEmail: PropTypes.func,
    pickAccountRecoverUsername: PropTypes.func,
    navigate: PropTypes.func,
  }).isRequired,
};

export default LoginFormFields;
